import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ID_PRESUPUESTO_CONFIRMADO, ID_PRESUPUESTO_PENDIENTE } from '../../constants/presupuesto.js'

import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { Spinner } from '../../ui/elements/Spinner/Spinner.jsx'
import { MensajeriaNew } from '../reutilizables/mensajeria/MensajeriaNew'
import { adjuntoBuilder } from '../reutilizables/upload/adjuntoBuilder'
import * as archivoAdjuntoService from '../servicios/archivoAdjuntoService'
import * as imagenesService from '../servicios/imagenesService'
import * as mensajesService from '../servicios/mensajesService'
import * as presupuestosService from '../servicios/presupuestosService'
import * as publicacionesService from '../servicios/publicacionesService'
import BordeDetalle from '../ui/BordeDetalle'
import styles from './Presupuesto.module.css'
import PresupuestoCabecera from './PresupuestoCabecera'
import PresupuestoForm from './PresupuestoForm'

const ID_ADJUNTOS_PRESUPUESTOS = 9

const DetallePresupuesto = () => {
  const { loginDataContext: { idUsuario: usuario, empid } = {} } = useAuth()
  const navigate = useNavigate()
  const { idPresupuesto } = useParams()
  const [estado, setEstado] = useState(null)
  const [estadoTxt, setEstadoTxt] = useState(null)
  const [mensajes, setMensajes] = useState([])
  const [presupuesto, setPresupuesto] = useState({})
  const [nombreAdjunto, setNombreAdjunto] = useState('')
  const [adjuntoPendiente, setAdjuntoPendiente] = useState(null)
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false)
  const [mediosPago, setMediosPagos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [avisoLimiteCompra, setAvisoLimiteCompra] = useState('')
  const presupuestoActualId = Number(idPresupuesto || 0)

  const esVendedor = !!(
    presupuesto.idVendedor &&
    (usuario === presupuesto.idVendedor ||
      (!!empid && !!presupuesto.empidVendedor && empid === presupuesto.empidVendedor))
  )

  const actualizarEstado = (estado) => {
    setEstado(ID_PRESUPUESTO_CONFIRMADO)
    setEstadoTxt(estado)
  }

  const buscarDetalle = async () => {
    const presupuesto = await presupuestosService.buscarPorId(idPresupuesto)

    if (presupuesto) {
      setPresupuesto(presupuesto.data)
      try {
        await buscarMensajes()
      } catch {
        setMensajes([])
      }
    } else {
      toastErrorLog(
        null,
        `No se encontró el presupuesto Nº ${idPresupuesto} o fue eliminado.`,
        'presupuestos'
      )
      navigate('/Presupuestos', { replace: true })
    }
    setCargando(false)
  }

  const buscarMensajes = async () => {
    const respuesta = await mensajesService.buscarPorPresupuesto(idPresupuesto)
    const data = respuesta?.data

    setMensajes(
      data?.length
        ? [...data].sort((a, b) => new Date(a.fechaEscrito) - new Date(b.fechaEscrito))
        : []
    )
  }

  const abrirAdjunto = async () => {
    try {
      if (
        (adjuntoPendiente?.idPresupuesto &&
          adjuntoPendiente.idPresupuesto !== presupuestoActualId) ||
        !adjuntoPendiente?.idAdjunto
      ) {
        return
      }

      const { data } = await archivoAdjuntoService.buscarAdjuntosPorId(adjuntoPendiente.idAdjunto)
      if (!data?.dataAdjunto || !data?.adjunto) {
        return
      }

      await adjuntoBuilder(data.dataAdjunto, data.adjunto, data.adjunto?.archivo || 'adjunto', {
        isBase64: true
      })
    } catch (error) {
      const mensaje = `Ocurrió un error al obtener el adjunto.`
      toastErrorLog(error, mensaje, 'presupuestos')
    }
  }

  const limpiarAdjunto = () => {
    setAdjuntoPendiente(null)
    setNombreAdjunto('')
  }

  const buscarPrimerAdjuntoPorUsuarios = async (idsUsuarios) => {
    try {
      for (const idUsuarioBusqueda of idsUsuarios) {
        const respuesta = await archivoAdjuntoService.buscarUltimoAdjunto(
          idUsuarioBusqueda,
          ID_ADJUNTOS_PRESUPUESTOS,
          idPresupuesto
        )
        const posibleAdjunto = respuesta?.data?.adjunto || respuesta?.data
        if (posibleAdjunto?.idAdjunto) {
          return respuesta.data
        }
      }
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al buscar el adjunto.', 'presupuestos')
    }

    return null
  }

  const buscarAdjuntosPorId = async (idAdjunto, nombreAdjunto = '') => {
    try {
      const { data: dataAdjunto } = await archivoAdjuntoService.buscarAdjuntosPorId(idAdjunto)
      const previewAdjunto = await adjuntoBuilder(
        dataAdjunto?.dataAdjunto,
        dataAdjunto?.adjunto,
        dataAdjunto?.adjunto?.archivo,
        { preview: true }
      )

      setAdjuntoPendiente({
        ...previewAdjunto,
        idAdjunto: idAdjunto,
        idPresupuesto: presupuestoActualId
      })
      setNombreAdjunto(previewAdjunto?.nombre || nombreAdjunto || '')
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al buscar el adjunto por ID.', 'presupuestos')
    }
  }

  const fetchAdjunto = async () => {
    const idsUsuarios = [presupuesto?.idVendedor, usuario, presupuesto?.idUsuario].filter(Boolean)
    const candidatosUnicos = [...new Set(idsUsuarios)]
    const data = await buscarPrimerAdjuntoPorUsuarios(candidatosUnicos)

    const adjunto = data?.adjunto || data
    if (!adjunto?.idAdjunto) {
      limpiarAdjunto()
      return
    }

    await buscarAdjuntosPorId(adjunto.idAdjunto, data?.nombre)
  }

  const handleCargaAdjunto = async (adjunto) => {
    try {
      setSubiendoAdjunto(true)

      const idAdjuntoNuevo =
        adjunto?.data?.idAdjunto || adjunto?.data?.id || adjunto?.data?.adjunto?.idAdjunto

      await buscarAdjuntosPorId(idAdjuntoNuevo)
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al procesar el adjunto.', 'presupuestos')
    } finally {
      setSubiendoAdjunto(false)
    }
  }

  const handleEliminarAdjunto = async () => {
    if (
      (adjuntoPendiente?.idPresupuesto && adjuntoPendiente.idPresupuesto !== presupuestoActualId) ||
      !adjuntoPendiente?.idAdjunto
    ) {
      limpiarAdjunto()
      return
    }

    try {
      await imagenesService.borrarImagen(adjuntoPendiente.idAdjunto)
      limpiarAdjunto()
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al eliminar el adjunto.', 'presupuestos')
    }
  }

  const obtenerMediosPago = async (idPublicacion, idPresupuesto) => {
    const { data: mediosPago } = await publicacionesService.BuscarMedioPagoPublicacion(
      idPublicacion,
      idPresupuesto
    )
    setMediosPagos(mediosPago)
  }

  useEffect(() => {
    if (!idPresupuesto) {
      return
    }

    limpiarAdjunto()
    buscarDetalle().catch(() => {
      setPresupuesto({})
      setCargando(false)
      navigate('/Presupuestos', { replace: true })
    })
  }, [idPresupuesto])

  useEffect(() => {
    if (!presupuesto?.idVendedor || !idPresupuesto || !usuario || !presupuesto?.idPublicacion) {
      return
    }
    fetchAdjunto()
    obtenerMediosPago(presupuesto.idPublicacion, idPresupuesto)
  }, [presupuesto?.idVendedor, presupuesto?.idPublicacion, idPresupuesto, usuario])

  if (cargando) {
    return <Spinner texto="Cargando presupuesto..." size="lg" />
  }

  return (
    <BordeDetalle
      titulo={'Detalle Presupuesto'}
      onCerrarClick={() => navigate(esVendedor ? '/Presupuestos' : '/PresupuestosComprador')}
    >
      <div className={esVendedor ? styles.contenedorGeneral : ''}>
        <PresupuestoCabecera
          presupuesto={presupuesto}
          estado={estado}
          estadoTxt={estadoTxt}
          esVendedor={esVendedor}
          abrirAdjunto={abrirAdjunto}
          nombreAdjunto={adjuntoPendiente?.nombre || nombreAdjunto}
          mediosPagoBase={mediosPago}
          avisoLimiteCompra={avisoLimiteCompra}
        />
        <PresupuestoForm
          idPresupuesto={idPresupuesto}
          presupuesto={presupuesto}
          handleEstado={actualizarEstado}
          idUsuario={usuario}
          puedeAdministrar={esVendedor && presupuesto.idEstado === ID_PRESUPUESTO_PENDIENTE}
          esVendedor={esVendedor}
          onGuardar={buscarDetalle}
          abrirAdjunto={abrirAdjunto}
          nombreAdjunto={nombreAdjunto}
          adjuntoPendiente={adjuntoPendiente}
          subiendoAdjunto={subiendoAdjunto}
          onCargaAdjunto={handleCargaAdjunto}
          onEliminarAdjunto={handleEliminarAdjunto}
          mediosPagoBase={mediosPago}
          setMediosPagosBase={setMediosPagos}
          onAvisoLimiteCompra={setAvisoLimiteCompra}
        />
      </div>
      <div className="mt-4">
        <MensajeriaNew
          titulo={'Mensajería interna'}
          idVendedor={presupuesto.idVendedor}
          idComprador={presupuesto.idUsuario}
          id={Number(idPresupuesto)}
          tipoDoc={'presupuesto'}
          mensajes={mensajes}
          tipoMensajes={'chat'}
          usuario={usuario}
          actualizarMensajes={buscarMensajes}
          empid={empid}
        />
      </div>
    </BordeDetalle>
  )
}

export default DetallePresupuesto
